import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Badge } from 'react-bootstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import './RankingQuestionDND.css';
import ErrorComponent from '../../../Components/ErrorMessage';
import { replacePlaceholder } from '../../../Utils/Utils';

function RankingQuestionDND({
  question,
  handleChange,
  formValues,
  showError,
  getChoiceOrder,
  setChoiceOrder,
}) {
  const isMandatory = question.isMandatory;
  const questionKey = question.tempId;
  const questionText = replacePlaceholder(question.questionText, formValues);

  /* ---------- RANDOMIZE refs (same pattern) ---------- */
  const seedRef = useRef({});

  const shuffleArrayWithSeed = (array, seed) => {
    const newArray = [...array];
    let random = seed;

    for (let i = newArray.length - 1; i > 0; i--) {
      random = (random * 9301 + 49297) % 233280;
      const j = Math.floor((random / 233280) * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Initialize items (UNCHANGED parsing logic)
  const [items, setItems] = useState(() => {
    // If saved value exists, use it
    if (formValues[questionKey] && Array.isArray(formValues[questionKey])) {
      return formValues[questionKey];
    }

    try {
      if (question.choices && question.choices.length > 0) {
        const firstChoice = question.choices[0];

        if (typeof firstChoice === "string") {
          return firstChoice.split(",").map(item => item.trim());
        }

        if (Array.isArray(firstChoice) && firstChoice.every(item => typeof item === "string")) {
          return [...question.choices];
        }

        if (typeof firstChoice === "object" && firstChoice.text) {
          return question.choices.map(choice => choice.text);
        }

        return question.choices.map(choice => JSON.stringify(choice));
      }
      return [];
    } catch (e) {
      console.error("Error parsing choices:", e);
      return [];
    }
  });

  const [isAnswerValidDND, setIsAnswerValidDND] = useState(true);
  const hasAnswer =
    formValues[questionKey] && formValues[questionKey].length > 0;

  /* ---------- RANDOMIZE once + freeze (ONLY new effect) ---------- */
  useEffect(() => {
    // use frozen order if exists
    const frozen = getChoiceOrder?.(questionKey);
    if (frozen) {
      setItems(frozen);
      return;
    }

    // do NOT randomize if already answered
    if (formValues[questionKey]?.length) return;

    let initialItems = [...items];

    if (question.choicesOrder === "random") {
      if (!seedRef.current[questionKey]) {
        seedRef.current[questionKey] = Math.random();
      }
      initialItems = shuffleArrayWithSeed(
        initialItems,
        seedRef.current[questionKey]
      );
    } else if (question.choicesOrder === "ascending") {
      initialItems.sort((a, b) => a.localeCompare(b));
    } else if (question.choicesOrder === "descending") {
      initialItems.sort((a, b) => b.localeCompare(a));
    }

    setItems(initialItems);
  }, [questionKey, question.choicesOrder]);

  /* ---------- Freeze order on first drag ---------- */
  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    // freeze order on first interaction
    const frozen = getChoiceOrder?.(questionKey);
    if (!frozen && setChoiceOrder) {
      setChoiceOrder(questionKey, items);
    }

    const itemsCopy = Array.from(items);
    const [reorderedItem] = itemsCopy.splice(result.source.index, 1);
    itemsCopy.splice(result.destination.index, 0, reorderedItem);

    setItems(itemsCopy);
    setIsAnswerValidDND(true);
    handleChange({
      target: { name: questionKey, value: itemsCopy },
    });
  };

  /* ---------- UI (UNCHANGED) ---------- */
  return (
    <Card className={`mt-3 ${isMandatory && showError && !hasAnswer ? "border-danger" : ""}`}>
      <Card.Body>
        <Form.Group>
          <div className="d-flex flex-column">
            <Form.Label>
              <b style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {questionKey}.{questionText}
                {isMandatory && <span className="text-danger">*</span>}
              </b>
            </Form.Label>

            <Form.Label>
              <b style={{ color: "lightblue" }}>
                {question.questionDescription}
              </b>
            </Form.Label>

            {isMandatory && showError && !hasAnswer && (
              <ErrorComponent message={question.requiredErrorText} />
            )}
          </div>

          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="items">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) => (
                    <Draggable key={item} draggableId={item} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="ranking-item d-flex align-items-center"
                        >
                          <div className="dots-container">
                            <span className="dots">&#8942;</span>
                            <span className="dots">&#8942;</span>
                            <span className="dots">&#8942;</span>
                          </div>
                          <div className="badge-box">
                            <Badge bg="secondary">{index + 1}</Badge>
                          </div>
                          <div className="item-container">{item}</div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Form.Group>
      </Card.Body>
    </Card>
  );
}


export default RankingQuestionDND;
